﻿import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Drawer, Box } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// config
import { HEADER, NAVBAR } from '../../../config';
// components
import { NavSectionVertical } from '../../../components/nav-section';
//
import navConfig from './NavConfig';
import CollapseButton from './CollapseButton';
import { m, useAnimation } from 'framer-motion';
import useScrolling from 'src/hooks/useScrolling';
import useWindowDimension from 'src/hooks/useWindowDimension';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { getScrollbarStyles } from 'src/theme/scrollbar';
import useImpersonateContext from 'src/hooks/useImpersonateContext';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

const HEADER_EXPANDED_HEIGHT =
  HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT + HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT;

const HEADER_COLLAPSED_HEIGHT =
  HEADER.DASHBOARD_DESKTOP_BANNER_HEIGHT_COLLAPSED +
  HEADER.DASHBOARD_DESKTOP_UNDER_BANNER_HEIGHT_COLLAPSED;

// ----------------------------------------------------------------------

type Props = {
  isOpenSidebar: boolean;
  onCloseSidebar: VoidFunction;
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }: Props) {
  const theme = useTheme();
  const { windowHeight } = useWindowDimension();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  // Stesso stato usato dall'header
  const { headerCollapsed } = useScrolling();
  const { bannerOffset } = useImpersonateContext();

  const { isCollapse, collapseClick, onToggleCollapse, onHoverLeave, onHoverEnter } =
    useCollapseDrawer();

  const nvabarBoxRef = useRef<any>();
  const buttonRef = useRef<any>();

  const buttonControls = useAnimation();
  const headerHeight = headerCollapsed ? HEADER_COLLAPSED_HEIGHT : HEADER_EXPANDED_HEIGHT;
  const navTarget = {
    marginTop: headerHeight + bannerOffset,
    height: windowHeight - headerHeight - bannerOffset,
  };

  useEffect(() => {
    buttonControls.start(isCollapse ? 'shorter' : 'expanded');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollapse]);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Box
      sx={{
        height: '100%',
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
        mt: 1,
        overflow: 'auto',
        ...getScrollbarStyles(theme),
      }}
    >
      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />
      <m.div
        variants={{
          expanded: { left: '205px' },
          shorter: { left: '75px' },
        }}
        initial="shorter"
        animate={buttonControls}
        transition={{ duration: 0.235 }}
        ref={buttonRef}
        onHoverStart={onHoverEnter}
        onBlur={onHoverLeave}
        style={{
          position: 'fixed',
          top: '95%',
          border: '1px solid #d1d7e0',
          boxSizing: 'border-box',
          borderRadius: '50%',
          background: '#f6f7f9',
        }}
      >
        {isDesktop && (
          <CollapseButton
            onToggleCollapse={onToggleCollapse}
            collapseClick={collapseClick}
            icon={<KeyboardArrowLeftIcon sx={{ height: '20px', width: '20px' }} />}
            rotation={'rotate(180deg)'}
          />
        )}
      </m.div>
    </Box>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              overflow: 'hidden',
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: '#f6f7f9',
              boxShadow: theme.customShadows.navbar,
              zIndex: 10,
              height: 1,
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: 300,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
            },
          }}
        >
          <m.div
            initial={false}
            animate={navTarget}
            transition={{ duration: 0.1 }}
            ref={nvabarBoxRef}
          >
            {renderContent}
          </m.div>
        </Drawer>
      )}
    </RootStyle>
  );
}